import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "bootstrap";
import logo1 from "../assets/CPA1.png";
import question from "../assets/helpcp.png";
import bell from "../assets/bellcp.png";
import bellActive from "../assets/Group.png";
import home from "../assets/homecp.png";
import "./Dashboard.css";
import { FiUser } from "react-icons/fi";
import { API_BASE } from "../apiConfig";

const NAV_PAGES = ["/Home", "/start-assessment", "/continue-assessment", "/Suggested-interventions", "/Goals", "/Library", "/Reminder"];
const HOME_NAV = [
  { label: "Home", to: "/Home" },
  {
    label: "Assessments",
    menu: "assessment",
    dropdown: [
      { label: "Start Initial Client Assessment", to: "/start-assessment?appointment=Initial&menu=assessment" },
      { label: "Continue Incomplete Assessment", to: "/continue-assessment?menu=assessment" },
    ],
  }, {
    label: "Goals",
    menu: "goals",
    dropdown: [
      { label: "Review Existing Goals", to: "/Goals?menu=goals" },
      { label: "Set New Goal for Existing Client", to: "/start-assessment?appointment=Review&menu=goals" },
    ],
  },
  { label: "Suggested Interventions", to: "/Suggested-interventions" },
  { label: "Library", to: "/Library" },
  { label: "Reminders", to: "/Reminder" },
];

const FULL_NAV = [
  { label: "Home", to: "/Home" },
  {
    label: "Assessments",
    menu: "assessment",
    dropdown: [
      { label: "Start Initial Client Assessment", to: "/start-assessment?appointment=Initial&menu=assessment" },
      { label: "Continue Incomplete Assessment", to: "/continue-assessment?menu=assessment" },
    ],
  }, {
    label: "Goals",
    menu: "goals",
    dropdown: [
      { label: "Review Existing Goals", to: "/Goals?menu=goals" },
      { label: "Set New Goal for Existing Client", to: "/start-assessment?appointment=Review&menu=goals" },
    ],
  },
  { label: "Suggested Interventions", to: "/Suggested-interventions" },
  { label: "Library", to: "/Library" },
  { label: "Reminders", to: "/Reminder" },
];

const HOME_NAV_PAGES = ["/Home"];

const Header = ({ onToggleMenu, menuOpen, setMenuOpen }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const popupRef = useRef(null);
  const detailRef = useRef(null);
  const detailCloseBtnRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/Home";

  // Effective menu state: support external props when provided,
  // otherwise manage the drawer internally (App renders <Header /> without props).
  const isMenuControlled = typeof setMenuOpen === "function";
  const isMenuOpen = isMenuControlled ? Boolean(menuOpen) : internalMenuOpen;

  const closeMenu = () => {
    if (isMenuControlled) {
      setMenuOpen(false);
    } else {
      setInternalMenuOpen(false);
    }
  };

  const handleMenuToggle = () => {
    if (typeof onToggleMenu === "function") {
      onToggleMenu();
    } else if (isMenuControlled) {
      setMenuOpen(prev => !prev);
    } else {
      setInternalMenuOpen(prev => !prev);
    }
  };

  // Close the mobile drawer when leaving the mobile viewport
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 767 && isMenuOpen) closeMenu();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMenuOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Fetch Notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE}/therapist_api/get_notifications.php`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.status === "success" && data.data) {
          setNotifications(data.data);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, []);

  // Helper to format date into user's local device/system timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let clean = String(dateString).trim();
      if (!clean.includes('T') && !clean.includes('+') && !clean.includes('Z')) {
        clean = clean.replace(' ', 'T') + '+11:00';
      }
      const date = new Date(clean);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleString([], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  // Mark a notification as read on the server
  const markNotificationRead = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await fetch(`${API_BASE}/therapist_api/mark_as_read.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ notification_id: id })
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === id ? { ...n, is_read: 1 } : n
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Get only the latest 100 notifications
  const visibleNotifications = notifications.slice(0, 100);

  // Calculate unread count based on visible notifications
  const unreadCount = visibleNotifications.filter(n => Number(n.is_read) !== 1).length;

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((el) => {
      if (!el._tooltipInstance) {
        const t = new Tooltip(el);
        el._tooltipInstance = t;
      }
    });
    return () => {
      tooltipTriggerList.forEach((el) => {
        if (el._tooltipInstance) {
          el._tooltipInstance.dispose();
          delete el._tooltipInstance;
        }
      });
    };
  }, []);

  // close user popup and notification popup if click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && !e.target.closest(".user-profile")) {
        setShowPopup(false);
      }
      if (!e.target.closest(".notification-popup") && !e.target.closest(".nav-icon-btn")) {
        setShowNotifications(false);
        setSelectedNotification(null);
      }
      if (!e.target.closest(".nav-item-dropdown")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user details from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userDetails = {
    name: user.first_name ? `${user.first_name} ${user.last_name || ""}` : "",
    email: user.email || "",
    profile_image: user.profile_image || null
  };

  // Construct full image URL if available
  const baseUrl = `${API_BASE}/uploads/therapists/`;
  const profileImageUrl = userDetails.profile_image ? `${baseUrl}${userDetails.profile_image}` : null;

  // Helper function to parse JWT
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Helper function to check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return false;
    return Date.now() >= decoded.exp * 1000;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("token");

    if (isTokenExpired(token)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user"); localStorage.removeItem("reminderLastToken"); localStorage.removeItem("reminderNextShowAt");
      navigate("/");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/therapist_api/therapist_logout.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.status) {
        localStorage.removeItem("token");
        localStorage.removeItem("user"); localStorage.removeItem("reminderLastToken"); localStorage.removeItem("reminderNextShowAt");
        navigate("/");
      } else {
        console.error("Logout failed:", data.message);
        localStorage.removeItem("token");
        localStorage.removeItem("user"); localStorage.removeItem("reminderLastToken"); localStorage.removeItem("reminderNextShowAt");
        navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("user"); localStorage.removeItem("reminderLastToken"); localStorage.removeItem("reminderNextShowAt");
      navigate("/");
    }
  };

  // -- SESSION EXPIRY ------------------------------------------------------
  // The "Session Expired" popup and 15-minute inactivity auto-logout have
  // been removed as requested. Components may still dispatch a
  // `session-expired` event; it is intentionally ignored here.
  // -----------------------------------------------------------------------

  // Handle clicking a notification item
  const handleNotificationClick = (n) => {
    setSelectedNotification(n);
    if (Number(n.is_read) !== 1) {
      markNotificationRead(n.notification_id);
    }
  };

  // Handler for Key Down events (Tab, Enter, Space)
  const handleKeyDown = (action, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  // Focus the close button when notification detail dialog opens
  useEffect(() => {
    if (selectedNotification && detailCloseBtnRef.current) {
      detailCloseBtnRef.current.focus();
    }
  }, [selectedNotification]);

  return (
    <>
      <header className="header shadow-sm bg-white" role="banner">
        {/* Mobile Toggler */}
        <button
          type="button"
          className="mobile-toggler"
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="primary-navigation"
          onClick={handleMenuToggle}
          onKeyDown={(e) => handleKeyDown(handleMenuToggle, e)}

        >
          <i className={`fas ${isMenuOpen ? "fa-times" : "fa-bars"}`} aria-hidden="true"></i>
        </button>

        {/* Logo */}
        <button
          type="button"
          className="header-logo d-flex align-items-center"
          onClick={() => navigate("/Home")}
          aria-label="CPA home"
        >
          <img src={logo1} alt="CPA Logo" className="header-logo-img" />
        </button>

        <div className="header-actions1 d-flex align-items-center">

          {/* Container for the 3 icons */}
          <div className="d-flex align-items-center gap-3">

            {/* Home Icon */}
            {!isHomePage && (
              <button
                className="nav-icon-btn"
                onClick={() => navigate("/Home")}
                aria-label="Home"
              >
                <img src={home} className="nav-icon-img" alt="" aria-hidden="true" />
                <span className="nav-icon-text">Home</span>
              </button>
            )}

            {/* Notification Icon */}
            <button
              className="nav-icon-btn notification-btn"
              onClick={() => { setShowNotifications(!showNotifications); setSelectedNotification(null); }}
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              aria-expanded={showNotifications}
            >
              <img
                src={showNotifications ? bellActive : bell}
                className="nav-icon-img"
                alt=""
                aria-hidden="true"
              />
              <span className="nav-icon-text">Notifications</span>

              {/* Notification Count Badge */}
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Help Icon */}
            <button
              className="nav-icon-btn"
              onClick={() => navigate("/Help")}
              aria-label="Help"
            >
              <img src={question} className="nav-icon-img" alt="" aria-hidden="true" />
              <span className="nav-icon-text">Help</span>
            </button>

          </div>

          {/* User Profile */}
          <button
            className={`action-item user-profile d-flex align-items-center ${showPopup ? "active" : ""}`}
            onClick={() => setShowPopup((p) => !p)}
            aria-label={`User Name ${userDetails.name}`}
            aria-expanded={showPopup}
          >
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt=""
                aria-hidden="true"
                className="user user-avatar"
              />
            ) : (
              <FiUser className="user" aria-hidden="true" />
            )}
            <span className="user-name">{userDetails?.name || ""}</span>
            <i className={`fas fa-caret-${showPopup ? "up" : "down"} ms-2 user-caret`} aria-hidden="true"></i>
          </button>
        </div>

        {/* User Popup */}
        {showPopup && (
          <div className="user-popup1" ref={popupRef} onKeyDown={(e) => { if (e.key === 'Escape') setShowPopup(false); }}>
            <div className="popup-content">
              <div className="details">
                <p className="user-popup-name">{userDetails.name}</p>
                <p>{userDetails.email}</p>
              </div>
              <button
                className="logout-link"
                onClick={() => { setShowPopup(false); handleLogout(); }}
                aria-label="Logout"
              >
                <div className="logout-btn">
                  <p className="logout-btn1">Logout</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Notification Popup Container */}
        {showNotifications && !selectedNotification && (
          <div className="notification-popup-wrapper" role="region" aria-label="Notifications" onKeyDown={(e) => { if (e.key === 'Escape') { setShowNotifications(false); setSelectedNotification(null); } }}>
            <div className="notification-popup shadow-lg">
              <div className="popup-header">
                <h2>Notifications</h2>
                <button
                  className="close-btn"
                  aria-label="Close notifications"
                  onClick={() => setShowNotifications(false)}
                >
                  &#10005;
                </button>
              </div>

              <div className="popup-body">
                {visibleNotifications.length > 0 ? (
                  visibleNotifications.map((n) => {
                    const isRead = Number(n.is_read) === 1;

                    return (
                      <div
                        key={n.notification_id}
                        className={`notification-item ${isRead ? '' : 'unread'}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Notification: ${n.message}`}
                        onClick={() => handleNotificationClick(n)}
                        onKeyDown={(e) => handleKeyDown(() => handleNotificationClick(n), e)}
                      >
                        <p className="notification-date">
                          {formatDate(n.sent_date)}
                        </p>

                        <p className="notification-text">
                          {n.message}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="notification-item">
                    <p className="notification-text">No notifications found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notification Detail Popup with Blur Background */}
        {selectedNotification && (
          <div
            className="notification-detail-overlay"
            ref={detailRef}
            role="dialog"
            aria-modal="true"
            aria-label="Notification details"
            onClick={() => setSelectedNotification(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSelectedNotification(null);
                return;
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                if (detailCloseBtnRef.current) {
                  detailCloseBtnRef.current.focus();
                }
              }
            }}
          >
            <div
              className="notification-detail-popup shadow"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="detail-header">
                <h2>Notification Details</h2>
                <button
                  className="detail-close-btn"
                  aria-label="Close notification detail"
                  ref={detailCloseBtnRef}
                  onClick={() => setSelectedNotification(null)}
                >
                  &#10005;
                </button>
              </div>
              <div className="notification-content">
                <p className="detail-text">{selectedNotification.message}</p>
                <p className="notification-date detail-date">{formatDate(selectedNotification.sent_date)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Session Expired Popup - removed per requirement */}
      </header>

      {/* Primary Navigation - varies by page */}
      {NAV_PAGES.includes(location.pathname) && (
        <>
          <nav className="navigation" aria-label="Main navigation" onKeyDown={(e) => { if (e.key === 'Escape') { closeMenu(); setOpenDropdown(null); } }}>
            {/* Plain lists of real links. No role="menubar"/"menuitem": those
                roles promise arrow-key navigation and make screen readers
                announce an application menu instead of "Home, link". */}
            <ul className={`nav-links list-unstyled ${isMenuOpen ? "open" : ""}`} id="primary-navigation">
              {(HOME_NAV_PAGES.includes(location.pathname) ? HOME_NAV : FULL_NAV).map((item) => {
                const isActive = location.pathname === item.to;
                const currentMenu = new URLSearchParams(location.search).get("menu");
                const isAssessmentActive = item.dropdown
                  ? currentMenu
                      ? currentMenu === item.menu
                      : (item.label === "Assessments" && location.pathname === "/continue-assessment") ||
                        (item.label === "Goals" && location.pathname === "/Goals")
                  : false;
                if (item.dropdown) {
                  return (
                    <li
                      key={item.label}
                      className={`nav-item nav-item-dropdown ${isAssessmentActive ? "active" : ""}`}
                    >
                      <button
                        type="button"
                        className="nav-item-dropdown-toggle"
                        aria-expanded={openDropdown === item.label}
                        aria-controls={`nav-dropdown-${item.menu}`}
                        onClick={() => {
                          setOpenDropdown(openDropdown === item.label ? null : item.label);
                          if (openDropdown === item.label) closeMenu();
                        }}
                      >
                        {item.label}
                        <i className={`fas fa-caret-${openDropdown === item.label ? "up" : "down"} nav-dropdown-caret`} aria-hidden="true"></i>
                      </button>
                      {openDropdown === item.label && (
                        <ul className={`nav-dropdown-menu list-unstyled${item.menu === "goals" ? " nav-dropdown-menu-right" : ""}`} id={`nav-dropdown-${item.menu}`}>
                          {item.dropdown.map((child) => {
                            // Both assessment links share /start-assessment, so
                            // compare the ?appointment= value too. Otherwise a
                            // screen reader would hear two "current page" links.
                            const [childPath, childQuery = ""] = child.to.split("?");
                            const childAppointment = new URLSearchParams(childQuery).get("appointment");
                            const childActive = location.pathname === childPath
                              && (!childAppointment || childAppointment === new URLSearchParams(location.search).get("appointment"));
                            return (
                              <li key={child.to}>
                                <Link
                                  to={child.to}
                                  className={`nav-dropdown-item ${childActive ? "active" : ""}`}
                                  aria-current={childActive ? "page" : undefined}
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    closeMenu();
                                  }}
                                >
                                  {child.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li
                    key={item.label}
                    className={`nav-item ${isActive ? "active" : ""}`}
                  >
                    <Link
                      to={item.to}
                      className="nav-item-link"
                      aria-current={isActive ? "page" : undefined}
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          {isMenuOpen && (
            <div
              className="menu-overlay"
              aria-hidden="true"
              onClick={closeMenu}
            ></div>
          )}
        </>
      )}
    </>
  );
};

export default Header;
